import { createContext, useContext, useState, useCallback, ReactNode } from 'react';

export interface BetSlipItem {
  id: string;
  market: string;
  matchup: string;
  pick: string;
  price: string;
  payout: string;
  profit: string;
  qty: number;
  badge?: string;
  badgeColor?: string;
  isSpread?: boolean;          // flag so the panel knows to show the adjuster
  baseSpread?: number;         // original numeric spread value (e.g. 3.5)
  baseProb?: number;           // original probability 0-1
}

interface BetSlipContextValue {
  items: BetSlipItem[];
  isOpen: boolean;
  spreadAdjs: Record<string, number>;   // betId -> adjustment value
  addBet: (bet: Omit<BetSlipItem, 'qty'>) => void;
  removeBet: (id: string) => void;
  updateQty: (id: string, qty: number) => void;
  updateSpreadAdj: (id: string, adj: number) => void;
  clearAll: () => void;
  openSlip: () => void;
  closeSlip: () => void;
  toggleSlip: () => void;
}

const BetSlipContext = createContext<BetSlipContextValue | null>(null);

export function BetSlipProvider({ children }: { children: ReactNode }) {
  const [items, setItems] = useState<BetSlipItem[]>([]);
  const [isOpen, setIsOpen] = useState(false);
  const [spreadAdjs, setSpreadAdjs] = useState<Record<string, number>>({});

  const addBet = useCallback((bet: Omit<BetSlipItem, 'qty'>) => {
    setItems(prev => {
      const existing = prev.find(i => i.id === bet.id);
      if (existing) {
        return prev.map(i => i.id === bet.id ? { ...i, qty: i.qty + 1 } : i);
      }
      return [...prev, { ...bet, qty: 1 }];
    });
    setIsOpen(true);
  }, []);

  const removeBet = useCallback((id: string) => {
    setItems(prev => prev.filter(i => i.id !== id));
    setSpreadAdjs(prev => {
      const next = { ...prev };
      delete next[id];
      return next;
    });
  }, []);

  const updateQty = useCallback((id: string, qty: number) => {
    if (qty <= 0) {
      setItems(prev => prev.filter(i => i.id !== id));
      setSpreadAdjs(prev => {
        const next = { ...prev };
        delete next[id];
        return next;
      });
    } else {
      setItems(prev => prev.map(i => i.id === id ? { ...i, qty } : i));
    }
  }, []);

  const updateSpreadAdj = useCallback((id: string, adj: number) => {
    setSpreadAdjs(prev => ({ ...prev, [id]: adj }));
  }, []);

  const clearAll = useCallback(() => {
    setItems([]);
    setSpreadAdjs({});
  }, []);
  const openSlip = useCallback(() => setIsOpen(true), []);
  const closeSlip = useCallback(() => setIsOpen(false), []);
  const toggleSlip = useCallback(() => setIsOpen(o => !o), []);

  return (
    <BetSlipContext.Provider value={{ items, isOpen, spreadAdjs, addBet, removeBet, updateQty, updateSpreadAdj, clearAll, openSlip, closeSlip, toggleSlip }}>
      {children}
    </BetSlipContext.Provider>
  );
}

export function useBetSlip() {
  const ctx = useContext(BetSlipContext);
  if (!ctx) throw new Error('useBetSlip must be used within BetSlipProvider');
  return ctx;
}
